import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  Check,
  Download,
  Edit,
  Eye,
  MessageCircle,
  Settings,
  ShieldCheck
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';

const chromeStoreUrl = 'https://chromewebstore.google.com/detail/nextshot-screenshot-tool/kaaloebdaodjlfecmlkjnheohmpcojhj';

const StatPill = ({ children }) => (
  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-700">
    <Check size={16} className="text-emerald-600" />
    <span className="font-medium">{children}</span>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description, colorClass }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
    <div className="flex items-start gap-4">
      <div className={`p-3 rounded-xl ${colorClass.bg} ${colorClass.text} flex-shrink-0`}>
        <Icon size={26} />
      </div>
      <div className="min-w-0">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

const StepCard = ({ number, title, description, icon: Icon }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 h-full">
    <div className="flex items-start gap-4 mb-4">
      <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 relative">
        <Icon size={22} />
        <div className="absolute -top-2 -right-2 w-7 h-7 bg-[#141414] text-white text-xs rounded-full flex items-center justify-center border-2 border-white">
          {number}
        </div>
      </div>
      <div className="min-w-0">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

const NextShotPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const features = useMemo(() => {
    const items = t('nextshot.features', { returnObjects: true });
    const config = [
      { icon: Eye, colorClass: { bg: 'bg-blue-50', text: 'text-blue-600' } },
      { icon: Settings, colorClass: { bg: 'bg-indigo-50', text: 'text-indigo-600' } },
      { icon: Activity, colorClass: { bg: 'bg-amber-50', text: 'text-amber-600' } },
      { icon: Edit, colorClass: { bg: 'bg-emerald-50', text: 'text-emerald-600' } },
      { icon: Check, colorClass: { bg: 'bg-purple-50', text: 'text-purple-600' } },
      { icon: Download, colorClass: { bg: 'bg-slate-50', text: 'text-slate-700' } }
    ];
    return items.map((item, idx) => ({ ...item, ...config[idx] }));
  }, [t]);

  const steps = useMemo(() => {
    const items = t('nextshot.how_steps', { returnObjects: true });
    const icons = [Eye, Edit, Download];
    return items.map((item, idx) => ({ ...item, icon: icons[idx] }));
  }, [t]);

  const privacyStats = useMemo(() => t('nextshot.privacy_grid', { returnObjects: true }), [t]);
  const benefitItems = useMemo(() => t('nextshot.benefit_items', { returnObjects: true }), [t]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <PageHeader />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center mb-20">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <ShieldCheck size={16} />
                {t('nextshot.privacy_badge')}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-6">
                {t('nextshot.title')}
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mb-8 font-medium">
                {t('nextshot.subtitle')}
              </p>

              <div className="flex flex-wrap gap-3 mb-10">
                {t('nextshot.pills', { returnObjects: true }).map((pill) => (
                  <StatPill key={pill}>{pill}</StatPill>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={chromeStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-full transition text-center"
                >
                  {t('nextshot.install_free')}
                </a>
                <a
                  href="#funciones"
                  className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-8 rounded-full transition text-center"
                >
                  {t('nextshot.cta_features')}
                </a>
              </div>

              <div className="mt-8 flex items-center gap-3 text-sm text-gray-500">
                <MessageCircle size={16} className="text-gray-400" />
                {t('nextshot.ideal_for')}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-blue-500/10 via-indigo-500/10 to-purple-500/10 blur-2xl rounded-3xl" />
              <div className="relative bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
                <img
                  src="/images/nextshot/1.png"
                  alt="Vista previa de NextShot"
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/1200x750/f3f4f6/111827?text=NextShot';
                  }}
                />
              </div>
            </div>
          </div>

          <section id="funciones" className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('nextshot.features_title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('nextshot.features_subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature) => (
                <FeatureCard
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.desc}
                  colorClass={feature.colorClass}
                />
              ))}
            </div>
          </section>

          <section className="mb-24">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nextshot.benefit_title')}</h2>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t('nextshot.benefit_subtitle')}
                  </p>
                  <div className="space-y-3">
                    {benefitItems.map((item) => (
                      <div key={item} className="flex items-start gap-3 text-gray-700">
                        <Check size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute -inset-6 bg-gradient-to-tr from-emerald-500/10 via-blue-500/10 to-purple-500/10 blur-2xl rounded-3xl" />
                  <div className="relative bg-gray-50 border border-gray-200 rounded-3xl overflow-hidden">
                    <img
                      src="/images/nextshot/2.png"
                      alt="Vista previa del editor de NextShot"
                      className="w-full h-auto"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://placehold.co/1200x750/f3f4f6/111827?text=Editor+NextShot';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('nextshot.how_title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('nextshot.how_subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((step, idx) => (
                <StepCard
                  key={step.title}
                  number={idx + 1}
                  icon={step.icon}
                  title={step.title}
                  description={step.desc}
                />
              ))}
            </div>
          </section>

          <section className="mb-24">
            <div className="bg-[#141414] text-white rounded-3xl p-8 md:p-12 relative overflow-hidden border border-white/10">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/35 via-indigo-900/25 to-purple-900/35" />
              <div className="relative grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                    <ShieldCheck size={16} />
                    {t('nextshot.privacy_tag', { defaultValue: 'Privacy by design' })}
                  </div>
                  <h2 className="text-3xl font-bold mb-4">{t('nextshot.privacy_title')}</h2>
                  <p className="text-white/80 leading-relaxed mb-6">
                    {t('nextshot.privacy_desc')}
                  </p>
                  <Link
                    to="/nextshot/privacy"
                    className="inline-flex bg-white/10 border border-white/15 text-white hover:bg-white/15 font-bold py-2.5 px-6 rounded-full transition mb-8"
                  >
                    {t('nextshot.privacy_link')}
                  </Link>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {privacyStats.map((item) => (
                      <div key={item.label} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                        <div className="text-sm text-white/70 font-semibold">{item.label}</div>
                        <div className="mt-2 text-lg font-bold">{item.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
                  <h3 className="text-xl font-bold mb-4">{t('nextshot.why_free_title')}</h3>
                  <p className="text-white/80 leading-relaxed">
                    {t('nextshot.why_free_desc')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nextshot.footer_title')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8">
              {t('nextshot.footer_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={chromeStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-10 rounded-full transition"
              >
                {t('nextshot.install_btn')}
              </a>
              <a
                href="#funciones"
                className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-10 rounded-full transition"
              >
                {t('nextshot.footer_btn_all')}
              </a>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default NextShotPage;
